import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, ArrowUpRight, XCircle, Settings } from "lucide-react";
import ManageSubscriptionModal from "../components/ManageSubscriptionModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLLS = 10;
const POLL_INTERVAL = 2000;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ payment_status: "pending" });
  const [pollCount, setPollCount] = useState(0);
  const [timedOut, setTimedOut] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);

  useEffect(() => {
    if (!sessionId) return;
    let stopped = false;

    async function poll(attempt) {
      if (stopped) return;
      if (attempt >= MAX_POLLS) {
        setTimedOut(true);
        return;
      }
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        setStatus(data);
        setPollCount(attempt + 1);
        if (data.payment_status === "paid") return;
        if (["expired", "failed"].includes(data.payment_status)) return;
        setTimeout(() => poll(attempt + 1), POLL_INTERVAL);
      } catch {
        setTimeout(() => poll(attempt + 1), POLL_INTERVAL);
      }
    }
    poll(0);
    return () => {
      stopped = true;
    };
  }, [sessionId]);

  const paid = status.payment_status === "paid";
  const failed = ["expired", "failed"].includes(status.payment_status);

  return (
    <div
      data-testid="payment-success-page"
      className="relative bg-white min-h-[70vh] border-b-2 border-[#1A1A1A]"
    >
      <div className="absolute inset-0 bg-grid opacity-50 pointer-events-none" />
      <div className="relative mx-auto max-w-3xl px-5 md:px-10 lg:px-12 py-24 md:py-32">
        <div className="rounded-3xl border-2 border-[#1A1A1A] bg-white p-8 md:p-12 nb-shadow-lg">
          {paid && (
            <>
              <div className="inline-flex items-center justify-center h-16 w-16 rounded-2xl bg-[#00C2B8] border-2 border-[#1A1A1A] nb-shadow-sm">
                <CheckCircle2 size={30} strokeWidth={2.5} />
              </div>
              <h1 className="mt-6 font-display font-black text-4xl md:text-5xl leading-tight tracking-tighter">
                Payment received. Welcome to TinkerShift.
              </h1>
              <p className="mt-4 text-[#4A4A4A] font-medium text-lg">
                A confirmation is on its way to your inbox. We’ll be in touch
                shortly to kick off onboarding.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/"
                  data-testid="payment-success-home-btn"
                  className="inline-flex items-center gap-2 bg-[#0055FF] text-white border-2 border-[#1A1A1A] rounded-full px-6 py-3 font-bold uppercase text-sm tracking-wide nb-shadow-sm nb-press"
                >
                  Back home <ArrowUpRight size={16} strokeWidth={2.5} />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-[#FFD700] text-[#1A1A1A] border-2 border-[#1A1A1A] rounded-full px-6 py-3 font-bold uppercase text-sm tracking-wide nb-shadow-sm nb-press"
                >
                  Send us your details
                </Link>
              </div>

              {/* Manage subscription card */}
              <div
                data-testid="payment-success-manage-card"
                className="mt-8 rounded-2xl border-2 border-[#1A1A1A] bg-[#F8F9FA] p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between"
              >
                <div className="flex items-start gap-3">
                  <span className="h-10 w-10 rounded-xl bg-white border-2 border-[#1A1A1A] flex items-center justify-center nb-shadow-sm">
                    <Settings size={18} strokeWidth={2.5} />
                  </span>
                  <div>
                    <p className="font-mono text-[11px] uppercase tracking-widest text-[#0055FF] font-bold">
                      Manage your subscription
                    </p>
                    <p className="mt-1 text-sm font-semibold">
                      Update card, download invoices, switch plans or cancel — any time.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setManageOpen(true)}
                  data-testid="payment-success-manage-btn"
                  className="inline-flex items-center gap-2 bg-[#1A1A1A] text-white border-2 border-[#1A1A1A] rounded-full px-5 py-2.5 font-bold uppercase text-xs tracking-widest nb-shadow-sm nb-press"
                >
                  Open portal <ArrowUpRight size={14} strokeWidth={2.5} />
                </button>
              </div>
            </>
          )}

          {!paid && !failed && !timedOut && (
            <>
              <div className="inline-flex items-center justify-center h-16 w-16 rounded-2xl bg-[#FFD700] border-2 border-[#1A1A1A] nb-shadow-sm">
                <Loader2 size={30} strokeWidth={2.5} className="animate-spin" />
              </div>
              <h1 className="mt-6 font-display font-black text-4xl md:text-5xl leading-tight tracking-tighter">
                Confirming your payment…
              </h1>
              <p className="mt-4 text-[#4A4A4A] font-medium text-lg">
                Hang tight — Stripe is finalising your transaction. This usually
                takes a few seconds. Attempt {pollCount + 1} / {MAX_POLLS}.
              </p>
            </>
          )}

          {(failed || timedOut) && (
            <>
              <div className="inline-flex items-center justify-center h-16 w-16 rounded-2xl bg-[#FF007F] text-white border-2 border-[#1A1A1A] nb-shadow-sm">
                <XCircle size={30} strokeWidth={2.5} />
              </div>
              <h1 className="mt-6 font-display font-black text-4xl md:text-5xl leading-tight tracking-tighter">
                Couldn’t confirm the payment.
              </h1>
              <p className="mt-4 text-[#4A4A4A] font-medium text-lg">
                {failed
                  ? "Your session expired or the payment failed. No charge was made."
                  : "We didn’t receive confirmation from Stripe in time. Please contact us and we’ll sort it."}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/pricing"
                  className="inline-flex items-center gap-2 bg-[#0055FF] text-white border-2 border-[#1A1A1A] rounded-full px-6 py-3 font-bold uppercase text-sm tracking-wide nb-shadow-sm nb-press"
                >
                  Try again
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-white text-[#1A1A1A] border-2 border-[#1A1A1A] rounded-full px-6 py-3 font-bold uppercase text-sm tracking-wide nb-shadow-sm nb-press"
                >
                  Contact us
                </Link>
              </div>
            </>
          )}

          {sessionId && (
            <p className="mt-8 font-mono text-[11px] text-[#4A4A4A]/70">
              Session: {sessionId.slice(0, 24)}…
            </p>
          )}
        </div>
      </div>
      <ManageSubscriptionModal open={manageOpen} onOpenChange={setManageOpen} />
    </div>
  );
}
